import { jsPDF } from "jspdf";
import { buildAiDisclosureXmp } from "@hightouch/lib/ad-studio/disclaimer/xmp";
import type { ImageFileType } from "src/pages/ad-studio/briefs/components/download-options";
import { captureException } from "src/lib/monitoring";

export type ReviewPdfPage = {
  /** Names the ad in warnings when it can't be added. */
  name: string;
  /** Printed under the ad, with its dimensions, so reviewers can tell ads apart. */
  label: string;
  image: Blob;
  machineReadableDisclosure: boolean;
};

type EncodedImage = { bytes: Uint8Array; format: "PNG" | "JPEG" };

/** One ad ready to place: its image, its label, and its size in image pixels. */
export type EncodedTile = {
  image: EncodedImage;
  label: EncodedImage;
  width: number;
  height: number;
};

/** Where one ad lands on a page, in points from the top left. */
export type PlacedTile = {
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
  labelX: number;
  labelY: number;
};

/** Label text colors, resolved from theme tokens by `useReviewPdfLabelColors`. */
export type ReviewPdfLabelColors = { name: string; dimensions: string };

export type ReviewPdfResult = {
  /** Null when no ad could be added. */
  blob: Blob | null;
  /** Names of ads left out of the PDF. */
  skipped: string[];
};

// US Letter landscape, in points.
const PAGE_WIDTH = 792;
const PAGE_HEIGHT = 612;
const PAGE_MARGIN = 36;
const GAP = 18;
// 1.5 in: the smallest an ad's cell gets before the grid drops a column.
const MIN_CELL_WIDTH = 108;
const LABEL_HEIGHT = 26;
const LABEL_FONT_SIZE = 8;
// Canvas pixels per point, so label text stays sharp when the PDF is zoomed.
const LABEL_RENDER_SCALE = 4;

const CONTENT_WIDTH = PAGE_WIDTH - PAGE_MARGIN * 2;
const CONTENT_HEIGHT = PAGE_HEIGHT - PAGE_MARGIN * 2;
const COLUMNS = Math.max(
  1,
  Math.floor((CONTENT_WIDTH + GAP) / (MIN_CELL_WIDTH + GAP)),
);
const CELL_WIDTH = (CONTENT_WIDTH - GAP * (COLUMNS - 1)) / COLUMNS;
// A square image box keeps tall banners from taking a whole row each.
const IMAGE_BOX_HEIGHT = Math.min(CELL_WIDTH, CONTENT_HEIGHT - LABEL_HEIGHT);

const LABEL_FONT_FAMILY =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const BACKGROUND_COLOR = "white";

/**
 * Builds one PDF for review: every ad on a grid of US Letter landscape pages,
 * each labeled with its name and dimensions. An ad that fails is skipped, so
 * one bad ad doesn't sink the document.
 */
export async function buildReviewPdf({
  pages,
  imageFileType,
  labelColors,
}: {
  pages: ReviewPdfPage[];
  imageFileType: ImageFileType;
  labelColors: ReviewPdfLabelColors;
}): Promise<ReviewPdfResult> {
  const tiles: EncodedTile[] = [];
  const skipped: string[] = [];
  for (const page of pages) {
    try {
      tiles.push(
        await encodeTile({
          image: page.image,
          label: page.label,
          imageFileType,
          labelColors,
        }),
      );
    } catch (err) {
      captureException(err);
      skipped.push(page.name);
    }
  }
  if (tiles.length === 0) return { blob: null, skipped };
  // jsPDF joins the whole document into one string, so a large export can pass V8's
  // string limit. A null blob keeps the export's other files.
  try {
    return {
      blob: assembleReviewPdf(tiles, {
        machineReadableDisclosure: pages.some(
          (page) => page.machineReadableDisclosure,
        ),
      }),
      skipped,
    };
  } catch (err) {
    captureException(err);
    return { blob: null, skipped };
  }
}

/**
 * Lays ads out in rows of equal-width cells, in order, starting a new page when
 * a row doesn't fit. Each ad shrinks to fit its cell's square box and never grows.
 */
export function layoutContactSheet(
  sizes: ReadonlyArray<{ width: number; height: number }>,
): PlacedTile[][] {
  const pages: PlacedTile[][] = [];
  let page: PlacedTile[] = [];
  let y = PAGE_MARGIN;

  for (let start = 0; start < sizes.length; start += COLUMNS) {
    const row = sizes.slice(start, start + COLUMNS).map((size) => {
      const scale = Math.min(
        1,
        CELL_WIDTH / size.width,
        IMAGE_BOX_HEIGHT / size.height,
      );
      return { width: size.width * scale, height: size.height * scale };
    });
    const imageHeight = Math.max(...row.map((tile) => tile.height));
    const rowHeight = imageHeight + LABEL_HEIGHT;

    if (page.length > 0 && y + rowHeight > PAGE_MARGIN + CONTENT_HEIGHT) {
      pages.push(page);
      page = [];
      y = PAGE_MARGIN;
    }

    row.forEach((tile, column) => {
      const cellX = PAGE_MARGIN + column * (CELL_WIDTH + GAP);
      page.push({
        index: start + column,
        x: cellX + (CELL_WIDTH - tile.width) / 2,
        y: y + (imageHeight - tile.height),
        width: tile.width,
        height: tile.height,
        labelX: cellX,
        labelY: y + imageHeight,
      });
    });
    y += rowHeight + GAP;
  }

  if (page.length > 0) pages.push(page);
  return pages;
}

/** Writes encoded ads onto contact-sheet pages, in order. */
export function assembleReviewPdf(
  tiles: EncodedTile[],
  { machineReadableDisclosure }: { machineReadableDisclosure: boolean },
): Blob {
  const pages = layoutContactSheet(tiles);
  if (pages.length === 0) throw new Error("assembleReviewPdf: no tiles");
  const doc = new jsPDF({
    unit: "pt",
    format: "letter",
    orientation: "landscape",
    compress: true,
  });
  pages.forEach((placed, pageIndex) => {
    if (pageIndex > 0) doc.addPage("letter", "landscape");
    for (const tile of placed) {
      const encoded = tiles[tile.index];
      if (!encoded) continue;
      addImage(doc, encoded.image, tile.x, tile.y, tile.width, tile.height);
      addImage(
        doc,
        encoded.label,
        tile.labelX,
        tile.labelY,
        CELL_WIDTH,
        LABEL_HEIGHT,
      );
    }
  });
  if (machineReadableDisclosure) {
    doc.addMetadata(buildAiDisclosureXmp(), true);
  }
  return doc.output("blob");
}

function addImage(
  doc: jsPDF,
  image: EncodedImage,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  // jsPDF re-deflates PNG pixels at its slowest level by default, too slow for a folder.
  doc.addImage(
    image.bytes,
    image.format,
    x,
    y,
    width,
    height,
    undefined,
    "MEDIUM",
  );
}

async function encodeTile({
  image,
  label,
  imageFileType,
  labelColors,
}: {
  image: Blob;
  label: string;
  imageFileType: ImageFileType;
  labelColors: ReviewPdfLabelColors;
}): Promise<EncodedTile> {
  const bitmap = await createImageBitmap(image);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    // JPEG has no alpha channel, so transparent areas flatten onto white.
    ctx.fillStyle = BACKGROUND_COLOR;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0);
    const isJpeg = imageFileType.type === "jpg";
    return {
      image: await encodeCanvas(
        canvas,
        isJpeg ? "JPEG" : "PNG",
        isJpeg ? imageFileType.quality : undefined,
      ),
      label: await encodeLabel(
        label,
        `${bitmap.width} × ${bitmap.height}`,
        labelColors,
      ),
      width: bitmap.width,
      height: bitmap.height,
    };
  } finally {
    bitmap.close();
  }
}

// Canvas text supports any script; jsPDF's built-in fonts only cover Latin.
async function encodeLabel(
  name: string,
  dimensions: string,
  colors: ReviewPdfLabelColors,
): Promise<EncodedImage> {
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(CELL_WIDTH * LABEL_RENDER_SCALE);
  canvas.height = Math.round(LABEL_HEIGHT * LABEL_RENDER_SCALE);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas context unavailable");
  ctx.fillStyle = BACKGROUND_COLOR;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const fontSize = LABEL_FONT_SIZE * LABEL_RENDER_SCALE;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.font = `600 ${fontSize}px ${LABEL_FONT_FAMILY}`;
  ctx.fillStyle = colors.name;
  ctx.fillText(
    fitText(ctx, name, canvas.width),
    canvas.width / 2,
    fontSize * 0.5,
  );
  ctx.font = `400 ${fontSize}px ${LABEL_FONT_FAMILY}`;
  ctx.fillStyle = colors.dimensions;
  ctx.fillText(dimensions, canvas.width / 2, fontSize * 1.8);

  return encodeCanvas(canvas, "PNG", undefined);
}

function fitText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  // Array.from splits by code point, so a trim never cuts an emoji or surrogate pair in half.
  const chars = Array.from(text);
  while (chars.length > 0) {
    chars.pop();
    const candidate = `${chars.join("").trimEnd()}…`;
    if (ctx.measureText(candidate).width <= maxWidth) return candidate;
  }
  return "";
}

async function encodeCanvas(
  canvas: HTMLCanvasElement,
  format: EncodedImage["format"],
  quality: number | undefined,
): Promise<EncodedImage> {
  const type = format === "JPEG" ? "image/jpeg" : "image/png";
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) =>
        result
          ? resolve(result)
          : reject(new Error(`canvas.toBlob gave no ${type}`)),
      type,
      quality,
    );
  });
  return { bytes: new Uint8Array(await blob.arrayBuffer()), format };
}
